"use client";

import { useState, useEffect } from "react";

interface CountdownTimerProps {
  label?: string;
  endsAt?: Date | string;
  mini?: boolean;
}

interface TimeLeft {
  days: number;
  h: number;
  m: number;
  s: number;
  total: number; // total seconds remaining
}

export function CountdownTimer({ label = "Offer ends in", endsAt, mini = false }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, h: 0, m: 0, s: 0, total: 0 });

  useEffect(() => {
    const getEnd = () => {
      if (endsAt) return new Date(endsAt).getTime();
      const end = new Date();
      end.setHours(23, 59, 59, 0);
      return end.getTime();
    };

    const calc = () => {
      const diff = Math.max(0, Math.floor((getEnd() - Date.now()) / 1000));
      setTimeLeft({
        total: diff,
        days: Math.floor(diff / 86400),
        h: Math.floor((diff % 86400) / 3600),
        m: Math.floor((diff % 3600) / 60),
        s: diff % 60,
      });
    };

    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  const pad = (n: number) => String(n).padStart(2, "0");
  const isMultiDay = timeLeft.total >= 86400;

  /* ── Mini mode (inside product cards) ── */
  if (mini) {
    return (
      <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
        style={{ background: "rgba(228,96,78,0.08)", border: "1px solid rgba(228,96,78,0.2)" }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#E4604E" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 1.5" />
        </svg>
        <span className="text-[10.5px] font-semibold" style={{ color: "#E4604E" }}>Ends in</span>

        {isMultiDay ? (
          /* > 24h: show "Xd Ymin" */
          <div className="flex items-center gap-1">
            <div className="flex flex-col items-center justify-center rounded-[5px] px-1.5 py-0.5"
              style={{ background: "#E4604E", minWidth: 26 }}>
              <span className="text-[11px] font-bold leading-none text-white">{timeLeft.days}d</span>
            </div>
            <div className="flex flex-col items-center justify-center rounded-[5px] px-1.5 py-0.5"
              style={{ background: "#E4604E", minWidth: 26 }}>
              <span className="text-[11px] font-bold leading-none text-white">{pad(timeLeft.m)}m</span>
            </div>
          </div>
        ) : (
          /* < 24h: show HH:MM:SS chips */
          <div className="flex items-center gap-0.5">
            {[
              { val: pad(timeLeft.h), unit: "HRS" },
              { val: pad(timeLeft.m), unit: "MIN" },
              { val: pad(timeLeft.s), unit: "SEC" },
            ].map(({ val, unit }, i) => (
              <div key={unit} className="flex items-center gap-0.5">
                {i > 0 && <span className="text-[10px] font-bold" style={{ color: "#E4604E", opacity: 0.5 }}>:</span>}
                <div className="flex flex-col items-center justify-center rounded-[5px] px-1.5 py-0.5"
                  style={{ background: "#E4604E", minWidth: 26 }}>
                  <span className="text-[11px] font-bold leading-none text-white">{val}</span>
                  <span className="text-[7px] font-bold uppercase tracking-wide text-white/60">{unit}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── Full mode (product page) ── */
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-2xl px-5 py-3.5"
      style={{ background: "rgba(228,96,78,0.07)", border: "1px solid rgba(228,96,78,0.2)" }}
    >
      <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color: "#E4604E" }}>
        {label}
      </span>

      {isMultiDay ? (
        /* > 24h: show day + min chips */
        <div className="flex items-center gap-1.5">
          {[
            { val: String(timeLeft.days), unit: "DAY" },
            { val: pad(timeLeft.m),       unit: "MIN" },
          ].map(({ val, unit }, i) => (
            <div key={unit} className="flex items-center gap-1.5">
              {i > 0 && <span className="text-[18px] font-bold leading-none" style={{ color: "#E4604E", opacity: 0.4 }}>:</span>}
              <div className="flex flex-col items-center justify-center rounded-[10px] px-3 py-2"
                style={{ background: "#E4604E", minWidth: 50 }}>
                <span className="text-[20px] font-bold leading-none tracking-tight text-white">{val}</span>
                <span className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-white/60">{unit}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* < 24h: show HRS:MIN:SEC chips */
        <div className="flex items-center gap-1.5">
          {[
            { val: pad(timeLeft.h), unit: "HRS" },
            { val: pad(timeLeft.m), unit: "MIN" },
            { val: pad(timeLeft.s), unit: "SEC" },
          ].map(({ val, unit }, i) => (
            <div key={unit} className="flex items-center gap-1.5">
              {i > 0 && <span className="text-[18px] font-bold leading-none" style={{ color: "#E4604E", opacity: 0.4 }}>:</span>}
              <div className="flex flex-col items-center justify-center rounded-[10px] px-3 py-2"
                style={{ background: "#E4604E", minWidth: 50 }}>
                <span className="text-[20px] font-bold leading-none tracking-tight text-white">{val}</span>
                <span className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-white/60">{unit}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
